import { useState, useEffect, useRef } from "react";
import { systemAPI } from "../utils/api";
import { useTheme } from "../context/ThemeContext";

const BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app");

const getAuthHeaders = () => {
  const token = localStorage.getItem("emr_token");
  return token ? { "Authorization": "Bearer " + token } : {};
};

function ProgressBar({ progress, total, label, color = "#4F46E5", running = true }) {
  const pct = total > 0 ? Math.min(Math.round((progress / total) * 100), 100) : (running ? null : 100);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#475569", marginBottom: 5 }}>
        <span>{label}</span>
        {pct !== null && <span style={{ fontWeight: 600 }}>{pct}%</span>}
      </div>
      <div style={{ height: 6, background: "#E4E8F0", borderRadius: 99, overflow: "hidden" }}>
        {pct !== null ? (
          <div style={{ height: "100%", background: color, borderRadius: 99, width: `${pct}%`, transition: "width 0.4s ease" }} />
        ) : (
          <div style={{ height: "100%", background: color, borderRadius: 99, width: "40%", animation: "progress-slide 1.5s ease-in-out infinite" }} />
        )}
      </div>
    </div>
  );
}

function BackupRestore() {
  const { t } = useTheme();
  const [backupStatus,   setBackupStatus]   = useState(null);
  const [restoreFile,    setRestoreFile]    = useState(null);
  const [restoreTab,     setRestoreTab]     = useState("r2");
  const [r2Backups,      setR2Backups]      = useState([]);
  const [r2Loading,      setR2Loading]      = useState(false);
  const [selectedR2Key,  setSelectedR2Key]  = useState("");
  const [polling,        setPolling]        = useState(false);
  const [msg,            setMsg]            = useState("");
  const [msgType,        setMsgType]        = useState("green");
  const [backupLoading,  setBackupLoading]  = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [backupDone,     setBackupDone]     = useState(false);
  const [restoreDone,    setRestoreDone]    = useState(false);
  const [showBackupResult,  setShowBackupResult]  = useState(false);
  const [showRestoreResult, setShowRestoreResult] = useState(false);
  const backupHideTimerRef  = useRef(null);
  const restoreHideTimerRef = useRef(null);
  const pollRef = useRef(null);

  const RESULT_VISIBLE_MS = 2 * 60 * 1000;

  const armResultTimer = (timerRef, hideSetter) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => hideSetter(false), RESULT_VISIBLE_MS);
  };

  const fetchStatus = async () => {
    try {
      const res  = await fetch(BASE_URL + "/backup/status", { headers: getAuthHeaders() });
      const data = await res.json();
      setBackupStatus(data);
      return data;
    } catch { return null; }
  };

  const fetchR2Backups = async () => {
    setR2Loading(true);
    try {
      const res  = await fetch(BASE_URL + "/backup/list-r2", { headers: getAuthHeaders() });
      const data = await res.json();
      const list = data.backups || [];
      setR2Backups(list);
      if (list.length > 0 && !selectedR2Key) setSelectedR2Key(list[0].key);
    } catch (e) {
      setMsg("Failed to load R2 backups: " + e.message); setMsgType("red");
    } finally { setR2Loading(false); }
  };

  useEffect(() => { fetchStatus(); }, []);
  useEffect(() => { if (restoreTab === "r2") fetchR2Backups(); }, [restoreTab]);
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (backupHideTimerRef.current) clearTimeout(backupHideTimerRef.current);
      if (restoreHideTimerRef.current) clearTimeout(restoreHideTimerRef.current);
    };
  }, []);

  const startPolling = (mode) => {
    setPolling(true);
    setBackupDone(false);
    setRestoreDone(false);
    pollRef.current = setInterval(async () => {
      const data = await fetchStatus();
      if (data && !data.backup?.running && !data.restore?.running) {
        clearInterval(pollRef.current);
        setPolling(false);
        setBackupLoading(false);
        setRestoreLoading(false);
        if (mode === "backup") {
          if (data.backup?.error) { setMsg("Backup failed: " + data.backup.error); setMsgType("red"); }
          else {
            setBackupDone(true); setShowBackupResult(true);
            armResultTimer(backupHideTimerRef, setShowBackupResult);
            setMsg("✅ Backup complete — uploaded to R2"); setMsgType("green");
            setTimeout(() => fetchStatus(), 500); fetchR2Backups();
          }
        }
        if (mode === "restore") {
          if (data.restore?.error && !data.restore?.summary) { setMsg("Restore failed: " + data.restore.error); setMsgType("red"); }
          else {
            setRestoreDone(true); setShowRestoreResult(true);
            armResultTimer(restoreHideTimerRef, setShowRestoreResult);
            setMsg("✅ Restore complete!"); setMsgType("green");
            setTimeout(() => fetchStatus(), 500);
          }
        }
      }
    }, 2000);
  };

  const handleCreateBackup = async () => {
    setMsg(""); setBackupLoading(true); setBackupDone(false);
    try {
      const res  = await fetch(BASE_URL + "/backup/create-stream", { method: "POST", headers: getAuthHeaders() });
      const data = await res.json();
      setMsg(data.message || "Backup started"); setMsgType("green");
      startPolling("backup");
    } catch (e) { setMsg("Backup failed: " + e.message); setMsgType("red"); setBackupLoading(false); }
  };

  const handleDownloadLatest = async () => {
    setMsg(""); setDownloadLoading(true);
    try {
      const tokenRes = await fetch(BASE_URL + "/auth/download-token", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + localStorage.getItem("emr_token") },
      });
      if (!tokenRes.ok) throw new Error("Could not get download token");
      const { token: dtok } = await tokenRes.json();
      window.open(BASE_URL + "/backup/download-latest?dtok=" + dtok, "_blank");
      setMsg("✅ Download started — check your downloads folder"); setMsgType("green");
    } catch (e) {
      setMsg("Download failed: " + e.message); setMsgType("red");
    } finally {
      setTimeout(() => setDownloadLoading(false), 1500);
    }
  };

  const handleRestoreFromR2 = async () => {
    if (!selectedR2Key) return;
    setMsg(""); setRestoreLoading(true); setRestoreDone(false);
    try {
      const res  = await fetch(BASE_URL + "/backup/restore-from-r2", {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ key: selectedR2Key }),
      });
      const data = await res.json();
      setMsg(data.message || "Restore started"); setMsgType("green");
      startPolling("restore");
    } catch (e) { setMsg("Restore failed: " + e.message); setMsgType("red"); setRestoreLoading(false); }
  };

  const handleRestoreFromPC = async () => {
    if (!restoreFile) return;
    setMsg(""); setRestoreLoading(true); setRestoreDone(false);
    const formData = new FormData();
    formData.append("file", restoreFile);
    try {
      const res  = await fetch(BASE_URL + "/backup/restore", { method: "POST", body: formData, headers: getAuthHeaders() });
      const data = await res.json();
      setMsg(data.message || "Restore started"); setMsgType("green");
      startPolling("restore");
    } catch (e) { setMsg("Restore failed: " + e.message); setMsgType("red"); setRestoreLoading(false); }
  };

  const isBackupRunning  = backupStatus?.backup?.running  || backupLoading;
  const isRestoreRunning = backupStatus?.restore?.running || restoreLoading;
  const lastBackup       = showBackupResult ? backupStatus?.backup?.last_file : null;
  const restoreSummary   = showRestoreResult ? backupStatus?.restore?.summary : null;
  const availableBackups = backupStatus?.available_backups || [];
  const backupStep       = backupStatus?.backup;
  const restoreStep      = backupStatus?.restore;
  const restoreError     = restoreStep?.error && !isRestoreRunning && !restoreSummary ? restoreStep.error : null;
  const restoreWarning   = restoreStep?.error && !isRestoreRunning && restoreSummary ? restoreStep.error : null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
      {/* Backup card */}
      <div className="card">
        <div className="card-header">
          <div style={{ fontWeight: 600, fontSize: 14 }}>{t.backup}</div>
          <button className="btn btn-ghost btn-sm" onClick={fetchStatus}>↻</button>
        </div>
        <div className="card-body">
          {msg && msgType !== "red" && <div className={"alert alert-" + msgType} style={{ marginBottom: 12, fontSize: 12 }}>{msg}</div>}
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>{t.streamingBackup}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn btn-primary btn-sm" onClick={handleCreateBackup} disabled={isBackupRunning || polling}>
              {isBackupRunning ? (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 12, height: 12, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
                  {t.creatingBackup}
                </span>
              ) : t.createBackup}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={handleDownloadLatest} disabled={downloadLoading || isBackupRunning}>
              {downloadLoading ? (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 12, height: 12, border: "2px solid currentColor", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
                  {t.downloading}
                </span>
              ) : t.downloadLatest}
            </button>
          </div>
          {isBackupRunning && (
            <ProgressBar progress={backupStep?.progress || 0} total={backupStep?.total || 0} label={backupStep?.step_label || "Starting backup..."} color="#4F46E5" running={true} />
          )}
          {backupDone && showBackupResult && !isBackupRunning && (
            <ProgressBar progress={100} total={100} label="✅ Backup complete — uploaded to R2" color="#16A34A" running={false} />
          )}
          {lastBackup && !isBackupRunning && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#16A34A" }}>
              ✓ {lastBackup}
              {backupStatus?.backup?.drive_uploaded ? " · ✓ R2" : " · ⚠ R2 upload failed"}
            </div>
          )}
          {availableBackups.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 8 }}>{t.onRailwayDisk}</div>
              {availableBackups.map((b, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "#F8FAFC", borderRadius: 8, marginBottom: 4, fontSize: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontFamily: "monospace", fontSize: 11 }}>{b.filename}</div>
                    <div style={{ color: "#64748B", fontSize: 11 }}>{b.created} · {b.size_mb} MB</div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={async () => {
                    try {
                      const res = await fetch(BASE_URL + "/backup/download/" + b.filename, { headers: getAuthHeaders() });
                      if (!res.ok) { setMsg("Download failed"); setMsgType("red"); return; }
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url; a.download = b.filename; a.click();
                      URL.revokeObjectURL(url);
                    } catch(e) { setMsg("Download failed: " + e.message); setMsgType("red"); }
                  }}>⬇</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Restore card */}
      <div className="card">
        <div className="card-header">
          <div style={{ fontWeight: 600, fontSize: 14 }}>{t.restoreSection}</div>
        </div>
        <div className="card-body">
          {msg && msgType === "red" && <div className="alert alert-red" style={{ marginBottom: 12, fontSize: 12 }}>{msg}</div>}
          <div className="alert alert-amber" style={{ marginBottom: 12, fontSize: 12 }}>
            {t.existingSkippedNote}
          </div>
          <div style={{ display: "flex", gap: 2, background: "#F1F5F9", borderRadius: 8, padding: 3, marginBottom: 14, width: "fit-content" }}>
            {[{ id: "r2", label: t.fromR2Btn }, { id: "pc", label: t.fromPCBtn }].map(tab => (
              <button key={tab.id} onClick={() => { setRestoreTab(tab.id); setMsg(""); }}
                style={{ padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600,
                  background: restoreTab === tab.id ? "#fff" : "transparent",
                  color: restoreTab === tab.id ? "#0F172A" : "#94A3B8",
                  boxShadow: restoreTab === tab.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  transition: "all 0.15s",
                }}>
                {tab.label}
              </button>
            ))}
          </div>
          {restoreTab === "r2" && (
            <div>
              {r2Loading ? (
                <div style={{ fontSize: 12, color: "#94A3B8" }}>Loading R2 backups...</div>
              ) : r2Backups.length === 0 ? (
                <div style={{ fontSize: 12, color: "#94A3B8" }}>
                  No backups in R2.
                  <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }} onClick={fetchR2Backups}>↻ Refresh</button>
                </div>
              ) : (
                <div>
                  <select value={selectedR2Key} onChange={e => setSelectedR2Key(e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #E4E8F0", fontSize: 12, background: "#fff", cursor: "pointer", marginBottom: 10 }}
                    disabled={isRestoreRunning}>
                    {r2Backups.map(b => (
                      <option key={b.key} value={b.key}>{b.filename} — {b.size_mb} MB · {b.created}</option>
                    ))}
                  </select>
                  <button className="btn btn-danger btn-sm" onClick={handleRestoreFromR2} disabled={isRestoreRunning || !selectedR2Key} style={{ width: "100%", justifyContent: "center" }}>
                    {isRestoreRunning ? (
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 12, height: 12, border: "2px solid currentColor", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
                        {t.restoring2}
                      </span>
                    ) : t.restoreFromR2}
                  </button>
                </div>
              )}
            </div>
          )}
          {restoreTab === "pc" && (
            <div>
              <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>Upload a backup zip from your PC.</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <label className="btn btn-secondary btn-sm" style={{ cursor: "pointer", opacity: isRestoreRunning ? 0.6 : 1 }}>
                  {restoreFile ? restoreFile.name : "📁 Select Zip"}
                  <input type="file" accept=".zip" style={{ display: "none" }} onChange={e => { setRestoreFile(e.target.files[0]); setMsg(""); }} disabled={isRestoreRunning} />
                </label>
                {restoreFile && (
                  <>
                    <button className="btn btn-danger btn-sm" onClick={handleRestoreFromPC} disabled={isRestoreRunning} style={{ minWidth: 120 }}>
                      {isRestoreRunning ? (
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ width: 12, height: 12, border: "2px solid currentColor", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
                          {t.restoring2}
                        </span>
                      ) : "▶ Restore Now"}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setRestoreFile(null); setMsg(""); }} disabled={isRestoreRunning} style={{ color: "#DC2626" }}>✕</button>
                  </>
                )}
              </div>
            </div>
          )}
          {isRestoreRunning && (
            <ProgressBar progress={restoreStep?.progress || 0} total={restoreStep?.total || 100} label={restoreStep?.step_label || "Processing restore..."} color="#4F46E5" running={true} />
          )}
          {restoreDone && showRestoreResult && !isRestoreRunning && (
            <ProgressBar progress={100} total={100} label="✅ Restore complete!" color="#16A34A" running={false} />
          )}
          {restoreError && (
            <div className="alert alert-red" style={{ marginTop: 10, fontSize: 12 }}>✗ Restore failed: {restoreError}</div>
          )}
          {restoreWarning && (
            <div className="alert alert-amber" style={{ marginTop: 10, fontSize: 12 }}>⚠ Completed with minor issue: {restoreWarning}</div>
          )}
          {restoreSummary && !isRestoreRunning && (
            <div style={{ marginTop: 10, padding: "12px 14px", background: "#F0FDF4", borderRadius: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: "#16A34A", marginBottom: 6 }}>✓ Restore Complete</div>
              {[
                ["Patients",      restoreSummary.patients],
                ["Reports",       restoreSummary.reports],
                ["Report Values", restoreSummary.report_values],
                ["Imaging",       restoreSummary.imaging],
                ["Report Files",  restoreSummary.report_files],
                ["Imaging Files", restoreSummary.imaging_files],
              ].map(([label, s]) => s && (
                <div key={label} style={{ display: "flex", gap: 16, marginBottom: 2 }}>
                  <span style={{ color: "#475569", minWidth: 100 }}>{label}:</span>
                  <span style={{ color: "#16A34A" }}>✓ {s.restored} restored</span>
                  {s.skipped > 0 && <span style={{ color: "#D97706" }}>{s.skipped} skipped</span>}
                  {s.failed  > 0 && <span style={{ color: "#DC2626" }}>{s.failed} failed</span>}
                </div>
              ))}
              {restoreSummary.errors?.length > 0 && (
                <div style={{ marginTop: 8, color: "#DC2626" }}>{restoreSummary.errors.length} minor error(s)</div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes progress-slide { 0% { transform: translateX(-100%); } 100% { transform: translateX(350%); } }
      `}</style>
    </div>
  );
}

function StorageCleanup() {
  const { t } = useTheme();
  const [scanning,     setScanning]     = useState(false);
  const [deleting,     setDeleting]     = useState(false);
  const [scanResult,   setScanResult]   = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);
  const [confirming,   setConfirming]   = useState(false);
  const [error,        setError]        = useState("");

  const handleScan = async () => {
    setScanning(true); setError(""); setDeleteResult(null); setConfirming(false);
    try {
      const res  = await fetch(BASE_URL + "/backup/orphaned-files", { headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Scan failed"); return; }
      setScanResult(data);
    } catch (e) { setError("Scan failed: " + e.message); }
    finally { setScanning(false); }
  };

  const handleDelete = async () => {
    setDeleting(true); setError("");
    try {
      const res  = await fetch(BASE_URL + "/backup/orphaned-files/delete", { method: "POST", headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Delete failed"); return; }
      setDeleteResult(data); setScanResult(null); setConfirming(false);
    } catch (e) { setError("Delete failed: " + e.message); }
    finally { setDeleting(false); }
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header">
        <div style={{ fontWeight: 600, fontSize: 14 }}>{t.storageCleanup}</div>
      </div>
      <div className="card-body">
        <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>
          Repeated restores can leave behind report/imaging files no longer linked to any record.
          Scan to see how many exist, then delete them to free up space.
        </div>
        {error && <div className="alert alert-red" style={{ marginBottom: 12, fontSize: 12 }}>{error}</div>}
        <button className="btn btn-secondary btn-sm" onClick={handleScan} disabled={scanning || deleting}>
          {scanning ? (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 12, height: 12, border: "2px solid currentColor", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
              {t.scanning}
            </span>
          ) : t.scanOrphaned}
        </button>
        {scanResult && (
          <div style={{ marginTop: 12, padding: "12px 14px", background: "#F8FAFC", borderRadius: 8, fontSize: 12 }}>
            <div style={{ display: "flex", gap: 16, marginBottom: 6 }}>
              <span><span style={{ fontWeight: 700, color: "#DC2626" }}>{scanResult.orphaned_count}</span> orphaned files</span>
              <span style={{ color: "#64748B" }}>({scanResult.orphaned_size_mb} MB)</span>
            </div>
            <div style={{ color: "#16A34A", marginBottom: 10 }}>{scanResult.kept_count} files are referenced and will be kept</div>
            {scanResult.orphaned_count === 0 ? (
              <div style={{ color: "#16A34A", fontWeight: 600 }}>✓ Nothing to clean up</div>
            ) : (
              <>
                {scanResult.sample_keys?.length > 0 && (
                  <details style={{ marginBottom: 10 }}>
                    <summary style={{ cursor: "pointer", color: "#475569" }}>Show sample filenames ({Math.min(20, scanResult.orphaned_count)} of {scanResult.orphaned_count})</summary>
                    <div style={{ marginTop: 6, fontFamily: "monospace", fontSize: 11, color: "#64748B", maxHeight: 150, overflowY: "auto" }}>
                      {scanResult.sample_keys.map((k, i) => <div key={i}>{k}</div>)}
                    </div>
                  </details>
                )}
                {!confirming ? (
                  <button className="btn btn-danger btn-sm" onClick={() => setConfirming(true)} disabled={deleting}>
                    🗑 Delete {scanResult.orphaned_count} Orphaned Files
                  </button>
                ) : (
                  <div className="alert alert-amber" style={{ fontSize: 12 }}>
                    <div style={{ marginBottom: 8 }}>Permanently delete {scanResult.orphaned_count} files ({scanResult.orphaned_size_mb} MB)? This cannot be undone.</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
                        {deleting ? (
                          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ width: 12, height: 12, border: "2px solid currentColor", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
                            Deleting...
                          </span>
                        ) : "Yes, Delete Permanently"}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setConfirming(false)} disabled={deleting}>Cancel</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {deleteResult && (
          <div style={{ marginTop: 12, padding: "12px 14px", background: "#F0FDF4", borderRadius: 8, fontSize: 12 }}>
            <div style={{ fontWeight: 700, color: "#16A34A", marginBottom: 4 }}>✓ Cleanup Complete</div>
            <div>Deleted {deleteResult.deleted_count} files, freed {deleteResult.freed_mb} MB</div>
            {deleteResult.errors?.length > 0 && (
              <div style={{ marginTop: 6, color: "#DC2626" }}>{deleteResult.errors.length} error(s) — some files may need a re-scan</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function HealthCard({ icon, label, value, sub, status, iconBg, iconColor }) {
  const pillStyle = {
    ok:      { bg: "#DCFCE7", color: "#166534" },
    warning: { bg: "#FEF3C7", color: "#92400E" },
    error:   { bg: "#FEE2E2", color: "#991B1B" },
  }[status] || { bg: "#F1F5F9", color: "#475569" };

  return (
    <div className="card" style={{ padding: "14px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: iconColor }}>
          {icon}
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 99, background: pillStyle.bg, color: pillStyle.color }}>
          {status === "ok" ? "Online" : status === "warning" ? "Warning" : "Error"}
        </span>
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A", marginBottom: 2 }}>{value}</div>
      <div style={{ fontSize: 12, color: "#64748B" }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

export default function SystemStatus() {
  const { t } = useTheme();
  const [status,    setStatus]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [errLoad,   setErrLoad]   = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const s = await systemAPI.status();
      setStatus(s);
      setLastCheck(new Date().toLocaleTimeString());
      setErrLoad(false);
    } catch { setErrLoad(true); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="empty-state" style={{ paddingTop: 64 }}><p>Loading system status...</p></div>;
  if (errLoad) return (
    <div>
      <div className="alert alert-red">Cannot connect to backend. Make sure the server is running.</div>
      <button className="btn btn-secondary btn-sm" onClick={load}>Retry</button>
    </div>
  );

  const isOk     = status?.status === "ok";
  const dbType   = status?.database?.type || "Unknown";
  const storage  = status?.storage?.backend || "unknown";
  const apiKey   = status?.ai?.api_key === "configured";
  const errors24 = status?.errors_24h ?? 0;

  const storageLabel = storage === "r2" ? "Cloudflare R2" : storage === "supabase" ? "Supabase" : "Local";
  const storageOk    = storage === "r2" || storage === "supabase";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 2 }}>System</h1>
          <p style={{ color: "#64748B", fontSize: 13 }}>Last checked: {lastCheck} · v{status?.version}</p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderRadius: 10, marginBottom: 20, background: isOk ? "#F0FDF4" : "#FEF2F2", border: `1px solid ${isOk ? "#BBF7D0" : "#FECACA"}`, fontSize: 14, fontWeight: 600, color: isOk ? "#166534" : "#991B1B" }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: isOk ? "#16A34A" : "#DC2626", flexShrink: 0 }} />
        {isOk ? t.allSystemsOk : t.systemIssue}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
        <HealthCard icon="🗄" label="Database" value={dbType} sub={`${status?.database?.patients ?? 0} patients · ${status?.database?.reports ?? 0} reports`} status={status?.database ? "ok" : "error"} iconBg="#E0FDF4" iconColor="#0D9488" />
        <HealthCard icon="☁" label="File storage" value={storageLabel} sub={storage === "r2" ? "Cloudflare R2 active" : storage === "supabase" ? "Supabase active" : "Local filesystem"} status={storageOk ? "ok" : "warning"} iconBg="#EDE9FE" iconColor="#7C3AED" />
        <HealthCard icon="🤖" label="AI extraction" value={apiKey ? "Active" : "Missing key"} sub={status?.ai?.model} status={apiKey ? "ok" : "error"} iconBg="#DCFCE7" iconColor="#16A34A" />
        <HealthCard icon="⚠" label="Errors (24h)" value={String(errors24)} sub={errors24 > 0 ? "Check error logs" : "No issues found"} status={errors24 > 0 ? "warning" : "ok"} iconBg={errors24 > 0 ? "#FEF3C7" : "#DBEAFE"} iconColor={errors24 > 0 ? "#D97706" : "#2563EB"} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div style={{ fontWeight: 600, fontSize: 14 }}>{t.systemInfo}</div>
          <a href="https://uptimerobot.com" target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">UptimeRobot →</a>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {[
            { label: "Version",     value: `v${status?.version || "3.0.0"}` },
            { label: "Backend",     value: "Railway · 2 workers" },
            { label: "Database",    value: dbType },
            { label: "Storage",     value: storageLabel },
            { label: "Monitor URL", value: `${BASE_URL}/health` },
          ].map(({ label, value }) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #F1F5F9", fontSize: 13 }}>
              <span style={{ color: "#64748B" }}>{label}</span>
              <span style={{ fontWeight: 600, color: "#0F172A", fontFamily: label === "Monitor URL" ? "monospace" : "inherit", fontSize: label === "Monitor URL" ? 11 : 13 }}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      <BackupRestore />
      <StorageCleanup />
    </div>
  );
}